import React from "react"
import PropTypes from "prop-types"
import {reactLocalStorage} from 'reactjs-localstorage'
import { colors } from '../lib/utilities'
import { addToCart } from '../actions/storage'

const SLATE = '#1a1a1a'
const SLATE_DEEP = '#0f0f0f'
const CHALK = '#f5f0e1'
const CREAM = '#e8d8a8'
const ACCENT = '#d4b86a'
const MUTED = '#9a9485'

const SERIF = "'Georgia', 'Times New Roman', serif"
const SANS = "'Helvetica Neue', Arial, sans-serif"

let styles = {
  modalDialog: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    maxHeight: '100%',
    margin: 0,
  },

  modalContent: {
    background: `radial-gradient(ellipse at center, ${SLATE} 0%, ${SLATE_DEEP} 100%)`,
    color: CHALK,
    border: 'none',
    minHeight: '100vh',
    fontFamily: SANS,
  },

  modalHeader: {
    background: 'transparent',
    border: 'none',
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
    padding: 12,
  },

  closeButton: {
    background: 'transparent',
    border: 'none',
    color: CHALK,
    fontSize: 32,
    lineHeight: 1,
    opacity: 1,
    cursor: 'pointer',
  },

  modalBody: {
    background: 'transparent',
    color: CHALK,
    border: 'none',
    padding: '60px 24px 32px',
  },

  titleWrap: {
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 6,
  },

  titleLine: {
    flex: 1,
    maxWidth: 60,
    height: 1,
    background: CHALK,
    opacity: 0.7,
  },

  title: {
    fontFamily: SERIF,
    fontStyle: 'italic',
    color: CHALK,
    fontSize: 36,
    fontWeight: 400,
    letterSpacing: 2,
    textTransform: 'uppercase',
    margin: 0,
    lineHeight: 1.1,
  },

  category: {
    textAlign: 'center',
    color: MUTED,
    fontSize: 11,
    letterSpacing: 4,
    textTransform: 'uppercase',
    marginBottom: 24,
  },

  imageWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '8px auto 24px',
  },

  circleImage: {
    width: 250,
    height: 250,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
    border: '3px solid #fff',
  },

  taccBadge: {
    height: 28,
    marginLeft: 8,
    verticalAlign: 'middle',
  },

  priceWrap: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 24,
    margin: '8px 0 24px',
  },

  pricePortion: {
    textAlign: 'center',
  },

  pricePortionLabel: {
    display: 'block',
    color: MUTED,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 4,
  },

  priceValue: {
    color: CREAM,
    fontSize: 30,
    fontWeight: 700,
    fontFamily: SERIF,
    letterSpacing: 1,
  },

  description: {
    color: MUTED,
    fontSize: 16,
    fontStyle: 'italic',
    lineHeight: 1.7,
    margin: '0 auto',
    maxWidth: 480,
    textAlign: 'center',
  },

  shareRow: {
    marginTop: 18,
    maxWidth: 480,
    marginLeft: 'auto',
    marginRight: 'auto',
    color: MUTED,
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },

  shareButton: {
    background: ACCENT,
    color: SLATE_DEEP,
    border: 'none',
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 12,
  },

  section: {
    maxWidth: 520,
    margin: '32px auto 0',
  },

  sectionHeader: {
    textAlign: 'center',
    marginBottom: 14,
  },

  sectionTitle: {
    fontFamily: SERIF,
    color: CHALK,
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 3,
    textTransform: 'uppercase',
    margin: 0,
  },

  sectionUnderline: {
    width: 50,
    height: 2,
    background: ACCENT,
    margin: '8px auto 0',
  },

  sectionHint: {
    color: MUTED,
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 6,
  },

  optionRow: {
    display: 'flex',
    alignItems: 'baseline',
    padding: '10px 4px',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: CHALK,
  },

  optionName: {
    color: CHALK,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 2,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '60%',
  },

  optionNameSelected: {
    color: CREAM,
  },

  optionDots: {
    flex: 1,
    margin: '0 8px',
    borderBottom: '2px dotted rgba(245,240,225,0.25)',
    transform: 'translateY(-4px)',
  },

  optionDotsSelected: {
    borderBottomColor: ACCENT,
  },

  optionPrice: {
    color: CREAM,
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },

  optionDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  },

  tasteRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 4px',
    borderBottom: '1px dotted rgba(245,240,225,0.15)',
  },

  tasteName: {
    color: CHALK,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },

  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },

}

class ItemMenue extends React.Component {
  state = {
    menue: null,
    quantity: 1,
    notes: '',
    selectedSides: [],
    selectedAdditionals: [],
    tastes: []
  }

  componentDidMount() {
    this.loadData()
  }

  addQuantity() {
    this.setState({ quantity: this.state.quantity + 1 })
  }

  setNotes(e) {
    this.setState({ notes: e.target.value })
  }

  removeQuantity() {
    const { quantity, tastes } = this.state
    if (quantity > 1) {
      tastes.forEach((t) => { t.quantity = 0 })
      this.setState({ quantity: quantity - 1, tastes })
    }
  }

  toggleSide(side) {
    const { selectedSides, menue } = this.state
    if (selectedSides.includes(side)) {
      this.setState({ selectedSides: selectedSides.filter(s => s !== side) })
    } else if (selectedSides.length < menue.max_sides_quantity) {
      this.setState({ selectedSides: [...selectedSides, side] })
    }
  }

  toggleAdditional(additional) {
    const { selectedAdditionals, menue } = this.state
    if (selectedAdditionals.includes(additional)) {
      this.setState({ selectedAdditionals: selectedAdditionals.filter(a => a !== additional) })
    } else if (selectedAdditionals.length < menue.max_additionals_quantity) {
      this.setState({ selectedAdditionals: [...selectedAdditionals, additional] })
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat("es-AR", {style: 'currency', currency: 'ARS'}).format(price)
  }

  async loadData() {
    let state = JSON.parse(reactLocalStorage.get('state'))
    const menue = state.restaurantDetails.categories.map((c) => c.menues).flat().find((m) => m.id === this.props.menueId)
    this.setState({ menue })
  }

  selectedTastes() {
    return this.state.tastes.reduce((memo, taste) => memo + (taste.quantity || 0), 0)
  }

  canAddTastes() {
    const { menue, quantity } = this.state
    return this.selectedTastes() < menue.menue_options_quantity * quantity
  }

  addTaste(taste) {
    if (!this.canAddTastes()) return
    const tastes = [...this.state.tastes]
    const index = tastes.findIndex((t) => t.id === taste.id)
    if (index > -1) {
      tastes[index].quantity = (tastes[index].quantity || 0) + 1
    } else {
      tastes.push({ ...taste, quantity: 1 })
    }
    this.setState({ tastes })
  }

  removeTaste(taste) {
    const tastes = [...this.state.tastes]
    const index = tastes.findIndex((t) => t.id === taste.id)
    if (index > -1 && tastes[index].quantity > 0) {
      tastes[index].quantity -= 1
      this.setState({ tastes })
    }
  }

  quantityForTaste(taste) {
    const found = this.state.tastes.find((t) => t.id === taste.id)
    return found ? (found.quantity || 0) : 0
  }

  renderPrice(menue) {
    if (menue.use_portion_price && menue.available_portions && menue.available_portions.length > 0) {
      return (
        <div style={styles.priceWrap}>
          {menue.available_portions.map(p => (
            <div key={`portion-${p.name}`} style={styles.pricePortion}>
              <span style={styles.pricePortionLabel}>{p.name}</span>
              <span style={styles.priceValue}>{this.formatPrice(p.price)}</span>
            </div>
          ))}
        </div>
      )
    }
    if (menue.price > 0) {
      return (
        <div style={styles.priceWrap}>
          <span style={styles.priceValue}>{this.formatPrice(menue.price)}</span>
        </div>
      )
    }
    return null
  }

  renderSectionHeader(title, hint) {
    return (
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>{title}</h3>
        <div style={styles.sectionUnderline} />
        {hint && <div style={styles.sectionHint}>{hint}</div>}
      </div>
    )
  }

  renderSides(menue) {
    if (!menue.has_sides || !menue.sides || menue.sides.length === 0) return null
    const { selectedSides } = this.state
    return (
      <div style={styles.section}>
        {this.renderSectionHeader('Guarniciones', `máximo ${menue.max_sides_quantity} item(s)`)}
        {menue.sides.map(side => {
          const isSelected = selectedSides.includes(side)
          const isDisabled = !isSelected && selectedSides.length >= menue.max_sides_quantity
          return (
            <button
              key={`side-${menue.id}-${side.id}`}
              type="button"
              style={{ ...styles.optionRow, ...(isDisabled ? styles.optionDisabled : {}) }}
              onClick={() => !isDisabled && this.toggleSide(side)}
            >
              <span style={isSelected ? { ...styles.optionName, ...styles.optionNameSelected } : styles.optionName}>
                {side.name}
              </span>
              <span style={isSelected ? { ...styles.optionDots, ...styles.optionDotsSelected } : styles.optionDots} />
              <span style={styles.optionPrice}>
                {side.price > 0 ? this.formatPrice(side.price) : (isSelected ? '✓' : '')}
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  renderTastes(menue) {
    if (!menue.has_tastes || !menue.tastes || menue.tastes.length === 0) return null
    return (
      <div style={styles.section}>
        {this.renderSectionHeader('Gustos / Sabores')}
        {menue.tastes.map(taste => {
          const qty = this.quantityForTaste(taste)
          return (
            <div key={`taste-${menue.id}-${taste.id}`} style={styles.tasteRow}>
              <span style={styles.tasteName}>{taste.title || taste.name}</span>
              
            </div>
          )
        })}
      </div>
    )
  }

  renderAdditionals(menue) {
    if (!menue.has_additionals || !menue.additionals || menue.additionals.length === 0) return null
    const { selectedAdditionals } = this.state
    return (
      <div style={styles.section}>
        {this.renderSectionHeader('Adicionales', `máximo ${menue.max_additionals_quantity} item(s)`)}
        {menue.additionals.map(additional => {
          const isSelected = selectedAdditionals.includes(additional)
          const isDisabled = !isSelected && selectedAdditionals.length >= menue.max_additionals_quantity
          return (
            <button
              key={`add-${menue.id}-${additional.id}`}
              type="button"
              style={{ ...styles.optionRow, ...(isDisabled ? styles.optionDisabled : {}) }}
              onClick={() => !isDisabled && this.toggleAdditional(additional)}
            >
              <span style={isSelected ? { ...styles.optionName, ...styles.optionNameSelected } : styles.optionName}>
                {additional.name}
              </span>
              <span style={isSelected ? { ...styles.optionDots, ...styles.optionDotsSelected } : styles.optionDots} />
              <span style={styles.optionPrice}>
                {additional.price > 0 ? this.formatPrice(additional.price) : (isSelected ? '✓' : '')}
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  render() {
    if (!this.state || !this.state.menue) {
      return <div></div>
    }

    const menue = this.state.menue
    const menueId = this.props.menueId
    const image = menue.image_original || this.props.restaurantImage

    const modalContentStyle = this.props.backgroundSrc
      ? {
          ...styles.modalContent,
          background: `linear-gradient(rgba(15,15,15,0.6), rgba(15,15,15,0.82)), url(${this.props.backgroundSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
        }
      : styles.modalContent

    return (
      <div className="modal fade" id={`item-menu-${menueId}`} tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable" style={styles.modalDialog} role="document">
          <div className="modal-content" style={modalContentStyle}>
            <div className="modal-header" style={styles.modalHeader}>
              <button type="button" className="close" style={styles.closeButton} data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="modal-body" style={styles.modalBody}>
              <div style={styles.titleWrap}>
                <span style={styles.titleLine} />
                <h1 style={styles.title}>
                  {menue.name}
                  {menue.tacc_free && (
                    <img src={this.props.imgSinTaccSrc} style={styles.taccBadge} alt="sin TACC" />
                  )}
                </h1>
                <span style={styles.titleLine} />
              </div>
              {this.props.categoryName && (
                <div style={styles.category}>— {this.props.categoryName} —</div>
              )}

              <div style={styles.imageWrap}>
                <div style={{ ...styles.circleImage, backgroundImage: `url(${image})` }} key={`menu-img-${menueId}`} />
              </div>

              {this.renderPrice(menue)}

              {menue.description && (
                <div style={styles.description}>{menue.description}</div>
              )}

              

              {this.renderSides(menue)}
              {this.renderTastes(menue)}
              {this.renderAdditionals(menue)}

            </div>
          </div>
        </div>
      </div>
    )
  }
}

ItemMenue.propTypes = {
  menueId: PropTypes.number,
  imgSinTaccSrc: PropTypes.string,
  restaurantImage: PropTypes.string,
  categoryName: PropTypes.string,
  restaurantId: PropTypes.number,
  backgroundSrc: PropTypes.string,
  onItemAdded: PropTypes.func
};

export default ItemMenue
